import { faCircleInfo, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useId, useMemo, useState } from 'react';

import { sanitizeHtml } from '../../helpers/sanitizeHtml';
import Icon from '../Icon/Icon';
import {
    DialogContentStyles,
    HeaderStyles,
    IconStyles,
    Label,
    ShowDetailsModalStyles,
} from './ShowDetailsModal.styles';

type Props = {
    label: string;
    dialogTitle: string;
    dialogContent: string;
};

/**
 * `dialogContent` is rendered as HTML, and since LP-2925 it can be a KYC question's `info` text rather than only
 * static translation copy, so it is sanitized first -- see {@link sanitizeHtml}.
 */
const ShowDetailsModal = ({ label, dialogTitle, dialogContent }: Props) => {
    const [open, setOpen] = useState(false);
    const safeDialogContent = useMemo(() => sanitizeHtml(dialogContent), [dialogContent]);
    // Several of these can share a page now that KYC questions carry their own info, so the ids cannot be constants:
    // duplicates would break aria-labelledby and aria-describedby.
    const dialogId = useId();
    const titleId = `${dialogId}-title`;
    const contentId = `${dialogId}-content`;

    return (
        <ShowDetailsModalStyles>
            <Label onClick={() => setOpen(true)} aria-controls={dialogId}>
                <span>{label}</span>
                <Icon icon={faCircleInfo} size="sm" />
            </Label>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={contentId}
                id={dialogId}>
                <DialogContent>
                    <HeaderStyles>
                        <DialogTitle id={titleId} sx={{ padding: '0' }}>
                            {dialogTitle}
                        </DialogTitle>
                        <IconButton aria-label="close dialog" onClick={() => setOpen(false)} sx={IconStyles}>
                            <Icon icon={faXmark} size="md" />
                        </IconButton>
                    </HeaderStyles>
                    <Box id={contentId} role="document" aria-live="polite" sx={DialogContentStyles}>
                        <span dangerouslySetInnerHTML={{ __html: safeDialogContent }} />
                    </Box>
                </DialogContent>
            </Dialog>
        </ShowDetailsModalStyles>
    );
};

export default ShowDetailsModal;
